<template>
  <div class="chart" ref="chart"></div>
</template>
<script>
import * as echarts from "echarts/core";
import { TooltipComponent, LegendComponent } from "echarts/components";
import { PieChart } from "echarts/charts";
import { LabelLayout } from "echarts/features";
import { SVGRenderer } from "echarts/renderers";
import { materialColor } from "../utils/constants";

echarts.use([
  TooltipComponent,
  LegendComponent,
  PieChart,
  SVGRenderer,
  LabelLayout,
]);

export default {
  props: ["items"],
  watch: {
    items() {
      if (this.myChart) {
        // notMerge: ECharts merges pie series.data by index, so a shorter new
        // dataset (filter narrows the breakdown) would leave the old trailing
        // slices on the chart. Replace the option wholesale instead.
        this.myChart.setOption(this.chartConfig, true);
      }
    },
    "$vuetify.theme.dark"() {
      if (this.myChart) {
        this.myChart.setOption(this.chartConfig, true);
      }
    },
  },

  computed: {
    chartConfig() {
      // ECharts is canvas/SVG and can't read CSS custom properties, so pull the
      // active theme's hexes straight from the runtime Vuetify theme (single
      // source of truth). The $vuetify.theme.dark watcher below re-applies this.
      const t = this.$vuetify.theme.dark
        ? this.$vuetify.theme.themes.dark
        : this.$vuetify.theme.themes.light;
      return {
        tooltip: {
          trigger: "item",
          backgroundColor: t.panel,
          borderColor: t.border,
          borderWidth: 1,
          textStyle: { color: t.textPrimary },
        },
        series: [
          {
            type: "pie",
            // Centred in the container; the wrapper controls the size now, so no
            // hardcoded bottom offset (which left the odd empty band below).
            center: ["50%", "50%"],
            radius: ["25%", "85%"],
            avoidLabelOverlap: false,
            label: {
              show: true,
              position: "inside",
              formatter: (data) => {
                if (data.percent >= 10) {
                  return parseInt(data.percent) + "%";
                }
                return "";
              },
              color: "#fff",
              fontSize: 14,
              fontWeight: "bold",
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 12,
                fontWeight: "bold",
              },
            },
            labelLine: {
              show: false,
            },
            data: this.items.map((item) => {
              return {
                ...item,
                itemStyle: {
                  color: materialColor(item.name),
                },
              };
            }),
          },
        ],
      };
    },
  },
  mounted() {
    this.myChart = echarts.init(this.$refs.chart);
    this.myChart.setOption(this.chartConfig);

    // Keep the chart sized to its container. ECharts doesn't track its own box,
    // so a ResizeObserver lets the wrapper drive width/height (incl. percentage
    // heights) — the old window-only listener missed container-driven changes.
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => {
        if (this._raf) return;
        this._raf = window.requestAnimationFrame(() => {
          this._raf = null;
          if (this.myChart) this.myChart.resize();
        });
      });
      this.ro.observe(this.$refs.chart);
    } else {
      this.onResize = () => this.myChart && this.myChart.resize();
      window.addEventListener("resize", this.onResize);
    }
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
    if (this.onResize) window.removeEventListener("resize", this.onResize);
    if (this._raf) window.cancelAnimationFrame(this._raf);
    if (this.myChart) this.myChart.dispose();
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/mixins.scss";
.chart {
  // Fill the wrapper; the parent (Statistics) sets the actual height as a
  // percentage of its container. min-height keeps it from collapsing to 0.
  width: 100%;
  height: 100%;
  min-height: 220px;
}
</style>
